import { Button, Center, Group, Skeleton, Stack, Table } from '@mantine/core'
import { PropsWithChildren, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

type CenteredTableRowProps = PropsWithChildren<{
  cols: number
  height?: number
}>

const CenteredTableRow = ({
  children,
  cols,
  height
}: CenteredTableRowProps) => {
  return (
    <Table.Tr>
      <Table.Td colSpan={cols}>
        <Center h={height} ta="center">
          {children}
        </Center>
      </Table.Td>
    </Table.Tr>
  )
}

const LoadingSkeleton = () => {
  return (
    <Group wrap="nowrap" gap="xl">
      <Skeleton height={32} width={60} />
      <Skeleton height={32} width={420} />
    </Group>
  )
}

type RenderTableStateProps = {
  hasItems?: boolean
  isLoading?: boolean
  hasError?: boolean
  hasActiveFilter?: boolean
  emptyMessage?: ReactNode
  errorMessage?: ReactNode
  cols: number
  onReload?: () => void
}

// This component renders different table states like loading, error, and empty states based on the provided props.
// It should be placed inside a table tbody.
export const RenderTableState = ({
  hasItems,
  isLoading,
  hasError,
  hasActiveFilter,
  emptyMessage,
  errorMessage,
  cols,
  onReload
}: RenderTableStateProps) => {
  // Early return if we have items - no special state needed
  if (hasItems) {
    return null
  }

  // Loading state takes precedence
  if (isLoading) {
    return (
      <Table.Tr>
        <Table.Td colSpan={cols}>
          <Stack>
            <LoadingSkeleton />
            <LoadingSkeleton />
          </Stack>
        </Table.Td>
      </Table.Tr>
    )
  }

  // Error state comes next
  if (hasError) {
    return (
      <CenteredTableRow cols={cols} height={120}>
        <Stack>
          {errorMessage || <FormattedMessage id="general.list.error" />}

          <div>
            <Button size="xs" variant="outline" onClick={onReload}>
              <FormattedMessage id="reload" />
            </Button>
          </div>
        </Stack>
      </CenteredTableRow>
    )
  }

  // Finally, empty state
  return (
    <CenteredTableRow cols={cols} height={120}>
      {hasActiveFilter ? (
        <FormattedMessage id="general.list.emptyAfterFilter" />
      ) : (
        emptyMessage || <FormattedMessage id="general.list.empty" />
      )}
    </CenteredTableRow>
  )
}
